// Helper function to extract a specific component type and return its long_name
const getAddressComponent = (addresses, type) => {
    const component = addresses.find(comp => comp.types.includes(type));
    return component ? component.long_name : '';
};

// Helper function to extract a component by type
const getComponentByType = (addresses, type) => {
    const component = addresses.find(comp => comp.types.includes(type));
    return component ? component.long_name : '';
};
// Process addresses in Western/Default format
export function processDefaultAddress(addresses, addressComponents) {
    // Street address (line 1)
    const streetNumber = getAddressComponent(addresses, 'street_number');
    const route = getAddressComponent(addresses, 'route');
    const premise = getAddressComponent(addresses, 'premise');

    if (streetNumber && route) {
        addressComponents.address_line_1 = `${streetNumber} ${route}`;
    } else if (route) {
        addressComponents.address_line_1 = route;
    } else if (premise) {
        addressComponents.address_line_1 = premise;
    }

    // Additional address info (line 2)
    const sublocality2 = getAddressComponent(addresses, 'sublocality_level_2');
    const sublocality1 = getAddressComponent(addresses, 'sublocality_level_1');

    if (sublocality2 && sublocality1 && sublocality2 !== sublocality1) {
        addressComponents.address_line_2 = `${sublocality2}, ${sublocality1}`;
    } else if (sublocality2) {
        addressComponents.address_line_2 = sublocality2;
    } else if (sublocality1) {
        addressComponents.address_line_2 = sublocality1;
    }

    // City
    const cityComponents = ['locality', 'postal_town', 'administrative_area_level_3', 'administrative_area_level_2'];
    for (const type of cityComponents) {
        const cityComponent = getComponentByType(addresses, type);
        if (cityComponent) {
            addressComponents.city = cityComponent;
            break;
        }
    }

    // State/Province
    const stateComponent = getComponentByType(addresses, 'administrative_area_level_1');
    if (stateComponent) {
        addressComponents.state = stateComponent;
    }

    // Postal/ZIP code
    const postalComponents = ['postal_code', 'postal_code_prefix'];
    for (const type of postalComponents) {
        const postalComponent = getComponentByType(addresses, type);
        if (postalComponent) {
            addressComponents.zipcode = postalComponent;
            break;
        }
    }
};

// Process Japanese addresses (reverse order compared to Western addresses)
export function processJapaneseAddress(addresses, addressComponents) {
    // In Japan, addresses go from largest to smallest: Prefecture, City, District, Block, Building
    addressComponents.state = getAddressComponent(addresses, 'administrative_area_level_1');
    addressComponents.city = getAddressComponent(addresses, 'locality') ||
        getAddressComponent(addresses, 'administrative_area_level_2');

    // District and further details become address_line_1
    const sublocality = getAddressComponent(addresses, 'sublocality_level_1');
    const sublocalityLevel2 = getAddressComponent(addresses, 'sublocality_level_2');
    const sublocalityLevel3 = getAddressComponent(addresses, 'sublocality_level_3');
    const sublocalityLevel4 = getAddressComponent(addresses, 'sublocality_level_4');

    // Combine components in order from larger to smaller areas
    const addressParts = [sublocality, sublocalityLevel2, sublocalityLevel3, sublocalityLevel4]
        .filter(Boolean)
        .join(' ');

    addressComponents.address_line_1 = addressParts;

    // Building name or premise becomes address_line_2
    addressComponents.address_line_2 = getAddressComponent(addresses, 'premise');

    // Postal code
    addressComponents.zipcode = getAddressComponent(addresses, 'postal_code');
};

// Process Korean addresses
export function processKoreanAddress(addresses, addressComponents) {
    // Similar structure to processJapaneseAddress with Korean-specific adjustments
    addressComponents.state = getAddressComponent(addresses, 'administrative_area_level_1');
    addressComponents.city = getAddressComponent(addresses, 'locality') ||
        getAddressComponent(addresses, 'administrative_area_level_2');

    // Korean addresses also typically go from larger areas to smaller
    const district = getAddressComponent(addresses, 'sublocality_level_1');
    const neighborhood = getAddressComponent(addresses, 'sublocality_level_2');
    const sublocality_level_4 = getAddressComponent(addresses, 'sublocality_level_4');

    addressComponents.address_line_1 = [district, neighborhood, sublocality_level_4].filter(Boolean).join(' ');
    addressComponents.address_line_2 = getAddressComponent(addresses, 'premise') ||
        getAddressComponent(addresses, 'street_number');

    addressComponents.zipcode = getAddressComponent(addresses, 'postal_code');
};

// Process Chinese addresses
export function processChineseAddress(addresses, addressComponents) {
    // China: Province, City, District, Street, Number
    addressComponents.state = getAddressComponent(addresses, 'administrative_area_level_1');
    addressComponents.city = getAddressComponent(addresses, 'locality') ||
        getAddressComponent(addresses, 'administrative_area_level_2');

    const district = getAddressComponent(addresses, 'sublocality_level_1');
    const street = getAddressComponent(addresses, 'route');
    const streetNumber = getAddressComponent(addresses, 'street_number');

    // Combine components appropriately for Chinese addresses
    addressComponents.address_line_1 = [district, street].filter(Boolean).join(' ');
    addressComponents.address_line_2 = streetNumber;

    addressComponents.zipcode = getAddressComponent(addresses, 'postal_code');
};

// Process Indian addresses
export function processIndianAddress(addresses, addressComponents) {
    addressComponents.state = getAddressComponent(addresses, 'administrative_area_level_1');
    addressComponents.city = getAddressComponent(addresses, 'locality');

    // In India, addresses often include sublocality, street name, and building name
    const sublocality = getAddressComponent(addresses, 'sublocality_level_1');
    const sublocality2 = getAddressComponent(addresses, 'sublocality_level_2'); // Often sector/block
    const sublocality3 = getAddressComponent(addresses, 'sublocality_level_3'); // Additional subdivision if available
    const street = getAddressComponent(addresses, 'route');
    const premise = getAddressComponent(addresses, 'premise');
    const streetNumber = getAddressComponent(addresses, 'street_number');

    // Combine components in a format suitable for Indian addresses
    if (streetNumber && street) {
        addressComponents.address_line_1 = `${streetNumber}, ${street}`;
    } else if (street) {
        addressComponents.address_line_1 = street;
    } else if (premise) {
        addressComponents.address_line_1 = premise;
    }
    // Build address_line_2 (typically area, sector, block information)
    let addressLine2Parts = [];

    if (sublocality3) {
        addressLine2Parts.push(sublocality3);
    }

    if (sublocality2) {
        addressLine2Parts.push(sublocality2);
    }

    if (sublocality) {
        addressLine2Parts.push(sublocality);
    }

    addressComponents.address_line_2 = addressLine2Parts.join(', ');
    addressComponents.zipcode = getAddressComponent(addresses, 'postal_code');
};